import React, { useEffect, useState } from 'react';
import { formatPhone } from '@/lib/utils';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Calendar, ArrowLeft, Loader2, Phone, Mail, User, CheckCircle2, XCircle,
  Clock, RefreshCw, ChevronDown,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import DateTimeField from '@/components/common/DateTimeField';
import { appointmentsAPI } from '@/lib/api';

const STATUS_META = {
  requested: { label: 'Requested', color: 'bg-slate-100 text-slate-700 border-slate-300' },
  confirmed: { label: 'Confirmed', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  rescheduled: { label: 'Rescheduled', color: 'bg-amber-100 text-amber-800 border-amber-300' },
  cancelled: { label: 'Cancelled', color: 'bg-rose-100 text-rose-700 border-rose-300' },
  completed: { label: 'Completed', color: 'bg-blue-100 text-blue-800 border-blue-300' },
  no_show: { label: 'No-show', color: 'bg-neutral-200 text-neutral-700 border-neutral-300' },
};

function formatLocal(iso, tz) {
  try {
    return new Date(iso).toLocaleString([], {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
      timeZone: tz || undefined,
    });
  } catch (_) {
    return iso;
  }
}

export default function AppointmentsPage() {
  const [params] = useSearchParams();
  const highlightId = params.get('highlight');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [rescheduling, setRescheduling] = useState(null);
  const [newIso, setNewIso] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await appointmentsAPI.list(statusFilter !== 'all' ? { status: statusFilter } : undefined);
      setItems(r.data.appointments || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [statusFilter]);

  const confirmOne = async (id) => {
    try { await appointmentsAPI.confirm(id); toast.success('Confirmed'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const cancelOne = async (id, appt) => {
    const emailNote = appt?.visitor?.email
      ? ` The visitor will receive an email notification at ${appt.visitor.email}.`
      : ' The visitor did not leave an email, so please contact them directly.';
    if (!window.confirm(`Cancel this appointment?${emailNote}`)) return;
    try { await appointmentsAPI.cancel(id); toast.success('Cancelled — visitor notified.'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const doReschedule = async () => {
    if (!rescheduling || !newIso) return;
    try {
      // Convert local datetime-local input to ISO UTC.
      const iso = new Date(newIso).toISOString();
      await appointmentsAPI.reschedule(rescheduling.appointment_id, iso);
      toast.success('Rescheduled');
      setRescheduling(null); setNewIso('');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const statusCounts = items.reduce((acc, a) => {
    acc[a.status] = (acc[a.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="page-content space-y-6" data-testid="appointments-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Link to="/leads" className="hover:text-slate-900 flex items-center gap-1">
              <ArrowLeft size={14} /> Leads
            </Link>
            <span>/</span>
            <span>Appointments</span>
          </div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900 mt-1 flex items-center gap-2">
            <Calendar className="h-6 w-6 text-sky-600" /> Appointments
          </h1>
          <p className="text-sm text-slate-500">
            Bookings from your landing pages — synced to your calendar and Google Calendar (if connected).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="appt-refresh">
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Status filters */}
      <div className="flex flex-wrap gap-2">
        <FilterChip active={statusFilter === 'all'} onClick={() => setStatusFilter('all')}>
          All ({items.length})
        </FilterChip>
        {Object.entries(STATUS_META).map(([k, m]) => (
          <FilterChip key={k} active={statusFilter === k} onClick={() => setStatusFilter(k)}>
            {m.label} ({statusCounts[k] || 0})
          </FilterChip>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 py-12 justify-center">
          <Loader2 className="animate-spin" size={18} /> Loading appointments…
        </div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-500">
            <Calendar className="h-10 w-10 mx-auto mb-3 text-slate-300" />
            <p>No appointments yet.</p>
            <p className="text-xs mt-1">
              Enable appointment booking on a Landing Page and share the link.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((a) => (
            <AppointmentRow
              key={a.appointment_id}
              appt={a}
              highlighted={a.appointment_id === highlightId}
              onConfirm={() => confirmOne(a.appointment_id)}
              onCancel={() => cancelOne(a.appointment_id, a)}
              onReschedule={() => {
                setRescheduling(a);
                // Prefill datetime-local with the current scheduled_at in local tz.
                const d = new Date(a.scheduled_at);
                const tz = a.timezone;
                try {
                  const parts = new Intl.DateTimeFormat('en-CA', {
                    timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
                    hour: '2-digit', minute: '2-digit', hour12: false,
                  }).formatToParts(d);
                  const m = Object.fromEntries(parts.map((p) => [p.type, p.value]));
                  setNewIso(`${m.year}-${m.month}-${m.day}T${m.hour}:${m.minute}`);
                } catch (_) {
                  setNewIso('');
                }
              }}
            />
          ))}
        </div>
      )}

      <Dialog open={!!rescheduling} onOpenChange={(o) => { if (!o) { setRescheduling(null); setNewIso(''); }}}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reschedule appointment</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Pick the new date and time. The visitor will receive an updated confirmation email
              (with a fresh <code>.ics</code> attachment that replaces the original entry in their calendar).
            </p>
            <DateTimeField
              value={newIso}
              onChange={setNewIso}
              testId="appt-reschedule-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRescheduling(null); setNewIso(''); }}>Cancel</Button>
            <Button onClick={doReschedule} disabled={!newIso} data-testid="appt-reschedule-confirm">
              Save new time
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FilterChip({ children, active, onClick }) {
  return (
    <button
      type="button" onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
        active
          ? 'bg-slate-900 text-white border-slate-900'
          : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
      }`}
    >
      {children}
    </button>
  );
}

function AppointmentRow({ appt, highlighted, onConfirm, onCancel, onReschedule }) {
  const [open, setOpen] = useState(highlighted || false);
  const meta = STATUS_META[appt.status] || STATUS_META.requested;
  const canConfirm = appt.status === 'requested';
  const canModify = ['requested', 'confirmed', 'rescheduled'].includes(appt.status);
  return (
    <Card className={highlighted ? 'ring-2 ring-sky-500' : ''} data-testid={`appt-row-${appt.appointment_id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-slate-900">
                {appt.visitor.first_name} {appt.visitor.last_name}
              </span>
              <Badge className={meta.color} variant="outline">{meta.label}</Badge>
              <span className="text-xs text-slate-500">
                {appt.duration_minutes} min · {appt.timezone}
              </span>
            </div>
            <div className="mt-1 flex items-center gap-2 text-sm text-slate-700">
              <Clock size={14} className="text-slate-400" />
              <span className="font-medium">{formatLocal(appt.scheduled_at, appt.timezone)}</span>
            </div>
            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
              <a href={`tel:${appt.visitor.phone}`} className="flex items-center gap-1 hover:text-slate-900">
                <Phone size={12} /> {formatPhone(appt.visitor.phone)}
              </a>
              {appt.visitor.email && (
                <a href={`mailto:${appt.visitor.email}`} className="flex items-center gap-1 hover:text-slate-900">
                  <Mail size={12} /> {appt.visitor.email}
                </a>
              )}
              {appt.lead_id && (
                <Link to={`/leads/${appt.lead_id}`} className="flex items-center gap-1 hover:text-slate-900">
                  <User size={12} /> View lead
                </Link>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {canConfirm && (
              <Button size="sm" onClick={onConfirm} data-testid={`appt-confirm-${appt.appointment_id}`}>
                <CheckCircle2 size={14} className="mr-1" /> Confirm
              </Button>
            )}
            {canModify && (
              <Button size="sm" variant="outline" onClick={onReschedule} data-testid={`appt-reschedule-${appt.appointment_id}`}>
                <RefreshCw size={14} className="mr-1" /> Reschedule
              </Button>
            )}
            {canModify && (
              <Button size="sm" variant="ghost" onClick={onCancel} className="text-red-600 hover:text-red-700"
                data-testid={`appt-cancel-${appt.appointment_id}`}>
                <XCircle size={14} className="mr-1" /> Cancel
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => setOpen((v) => !v)}>
              <ChevronDown size={14} className={`transition ${open ? 'rotate-180' : ''}`} />
            </Button>
          </div>
        </div>
        {open && (
          <div className="mt-3 pt-3 border-t border-slate-100 text-sm space-y-2">
            {appt.visitor.notes && (
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Notes</div>
                <p className="text-slate-700 mt-1 whitespace-pre-wrap">{appt.visitor.notes}</p>
              </div>
            )}
            {(appt.reschedule_history || []).length > 0 && (
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Reschedule history</div>
                <ul className="text-xs text-slate-600 mt-1 space-y-0.5">
                  {appt.reschedule_history.map((h, i) => (
                    <li key={i}>
                      {formatLocal(h.from, appt.timezone)} → {formatLocal(h.to, appt.timezone)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
